import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaIdCard,
  FaTicketAlt,
  FaArrowRight,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    phone: "",
    referralCode: referralCode,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    const { confirmPassword, ...registrationData } = formData;
    const result = await register(registrationData);

    if (result.success) {
      navigate("/dashboard");
    }

    setLoading(false);
  };

  const fields = [
    {
      name: "fullName",
      label: "Full Name",
      type: "text",
      icon: FaIdCard,
      placeholder: "John Doe",
    },
    {
      name: "username",
      label: "Username",
      type: "text",
      icon: FaUser,
      placeholder: "johndoe",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      icon: FaEnvelope,
      placeholder: "you@example.com",
    },
  ];

  const passwordFields = [
    {
      name: "password",
      label: "Password",
      icon: FaLock,
      placeholder: "Min 6 characters",
      show: showPassword,
      toggle: () => setShowPassword(!showPassword),
    },
    {
      name: "confirmPassword",
      label: "Confirm Password",
      icon: FaLock,
      placeholder: "Re-enter password",
      show: showConfirmPassword,
      toggle: () => setShowConfirmPassword(!showConfirmPassword),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden flex flex-col">
      {/* Background effects */}
      <div className="absolute inset-0 futuristic-grid opacity-20"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-b from-violet-500/25 via-cyan-500/15 to-transparent blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-cyan-600/10 blur-3xl rounded-full"></div>
      <div className="absolute bottom-40 left-0 w-48 h-48 bg-violet-500/10 blur-3xl rounded-full"></div>

      {/* Top branding area */}
      <div className="relative z-10 flex flex-col items-center pt-12 pb-6 sm:pt-16 sm:pb-8">
        <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-[0_0_40px_rgba(139,92,246,0.4)] mb-4">
          <span className="text-white font-bold text-xl sm:text-2xl">L</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Create Account
        </h1>
        <p className="text-sm text-slate-400 mt-1">Join the LOOPS community</p>
      </div>

      {/* Form area */}
      <div className="relative z-10 flex-1 flex flex-col px-6 sm:px-8 max-w-md w-full mx-auto pb-6">
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Text fields: fullName, username, email */}
          {fields.map((field) => {
            const Icon = field.icon;
            const hasError = errors[field.name];
            const isFocused = focusedField === field.name;

            return (
              <div key={field.name}>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  {field.label}
                </label>
                <div
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                    hasError ? "border-rose-500/60 bg-rose-500/[0.05]"
                    : isFocused ?
                      "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                    : "border-white/10 bg-white/[0.04]"
                  }`}
                >
                  <Icon
                    className={`text-sm flex-shrink-0 transition-colors ${
                      hasError ? "text-rose-400"
                      : isFocused ? "text-cyan-400"
                      : "text-slate-500"
                    }`}
                  />
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    onFocus={() => setFocusedField(field.name)}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                    placeholder={field.placeholder}
                    autoComplete="off"
                    required
                  />
                </div>
                {hasError && (
                  <p className="text-rose-400 text-xs mt-1.5 pl-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            );
          })}

          {/* Phone field with country selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Phone
            </label>
            <div
              className={`phone-input-wrapper flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                errors.phone ? "border-rose-500/60 bg-rose-500/[0.05]"
                : focusedField === "phone" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <PhoneInput
                international
                defaultCountry="US"
                value={formData.phone}
                onChange={(value) => {
                  setFormData({ ...formData, phone: value || "" });
                  if (errors.phone) {
                    setErrors({ ...errors, phone: "" });
                  }
                }}
                onFocus={() => setFocusedField("phone")}
                onBlur={() => setFocusedField(null)}
                className="flex-1"
                placeholder="Enter phone number"
              />
            </div>
            {errors.phone && (
              <p className="text-rose-400 text-xs mt-1.5 pl-1">
                {errors.phone}
              </p>
            )}
          </div>

          {/* Password fields with eye toggle */}
          {passwordFields.map((field) => {
            const Icon = field.icon;
            const hasError = errors[field.name];
            const isFocused = focusedField === field.name;

            return (
              <div key={field.name}>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                  {field.label}
                </label>
                <div
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                    hasError ? "border-rose-500/60 bg-rose-500/[0.05]"
                    : isFocused ?
                      "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                    : "border-white/10 bg-white/[0.04]"
                  }`}
                >
                  <Icon
                    className={`text-sm flex-shrink-0 transition-colors ${
                      hasError ? "text-rose-400"
                      : isFocused ? "text-cyan-400"
                      : "text-slate-500"
                    }`}
                  />
                  <input
                    type={field.show ? "text" : "password"}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    onFocus={() => setFocusedField(field.name)}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                    placeholder={field.placeholder}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={field.toggle}
                    className="text-slate-500 hover:text-slate-300 transition-colors touch-manipulation p-1"
                    tabIndex={-1}
                  >
                    {field.show ?
                      <FaEyeSlash className="text-sm" />
                    : <FaEye className="text-sm" />}
                  </button>
                </div>
                {hasError && (
                  <p className="text-rose-400 text-xs mt-1.5 pl-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            );
          })}

          {/* Referral code */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
              Referral Code
            </label>
            <div
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                focusedField === "referralCode" ?
                  "border-cyan-400/60 bg-white/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.1)]"
                : "border-white/10 bg-white/[0.04]"
              }`}
            >
              <FaTicketAlt
                className={`text-sm flex-shrink-0 transition-colors ${
                  focusedField === "referralCode" ? "text-cyan-400" : (
                    "text-slate-500"
                  )
                }`}
              />
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
                onFocus={() => setFocusedField("referralCode")}
                onBlur={() => setFocusedField(null)}
                className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Submit button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-violet-500 via-sky-500 to-cyan-500 text-white font-semibold rounded-xl shadow-[0_0_30px_rgba(139,92,246,0.35)] hover:shadow-[0_0_40px_rgba(56,189,248,0.5)] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation text-sm"
            >
              {loading ?
                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              : <>
                  Create Account
                  <FaArrowRight className="text-xs" />
                </>
              }
            </button>
          </div>
        </form>

        {/* Bottom link */}
        <div className="py-6 text-center">
          <p className="text-sm text-slate-400">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
